import clsx from "clsx";
import { Check, ChevronDown, Search } from "lucide-react";
import { type CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import { SyncBadge } from "./SyncBadge";

/*
 * Choosing a record that can be in ServiceM8 or only in this app (a supplier, an item): like a
 * dropdown, but each choice shows the sync icon before its name (a native <select> can't show
 * icons). A search box appears once there are more than a handful, ready to type in as it opens (names and their detail, e.g. an
 * item number, searched). Its list is placed on the screen
 * (fixed), under it or above it, wherever there's more room, so a card or window around it that
 * clips what overflows it can't cut it off; it closes when what's around it scrolls.
 */

export interface SyncOption {
    value: string;
    label: string;
    /** Where it lives (its sync icon); left out: a record that's always in ServiceM8 (e.g. a Price List item), shown without one. */
    synced?: boolean;
    /** It isn't a record (e.g. "Cheapest"): no sync icon, its name lined up with theirs. */
    notRecord?: boolean;
    /** Smaller text after the name, e.g. "archived". */
    detail?: string;
}

export function SyncSelect(
    props: Readonly<{
        options: SyncOption[];
        value: string;
        onChange: (value: string) => void;
        placeholder?: string;
        disabled?: boolean;
        className?: string;
        /** Its label, for screen readers. */
        label: string;
    }>,
) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");
    const box = useRef<HTMLDivElement>(null);
    const list = useRef<HTMLDivElement>(null);
    // Where its list goes (worked out as it opens): under it, or above it when there's more room there.
    const [place, setPlace] = useState<CSSProperties>({});
    const openList = () => {
        const rect = box.current?.getBoundingClientRect();
        if (rect) {
            const below = innerHeight - rect.bottom - 8;
            const above = rect.top - 8;
            const down = below >= 200 || below >= above;
            const room = Math.max(120, Math.min(288, down ? below : above));
            setPlace({ left: rect.left, width: rect.width, maxHeight: room, ...(down ? { top: rect.bottom + 4 } : { bottom: innerHeight - rect.top + 4 }) });
        }
        setOpen(true);
    };
    const current = props.options.find((o) => o.value === props.value);
    const shown = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? props.options.filter((o) => `${o.label} ${o.detail ?? ""}`.toLowerCase().includes(q)) : props.options;
    }, [props.options, query]);

    useEffect(() => {
        if (!open) return;
        // Pointer, not mouse: a window's title bar and edges cancel the mouse events as they're dragged (it'd be left open behind).
        const onDown = (e: PointerEvent) => !box.current?.contains(e.target as Node) && setOpen(false);
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Escape") return;
            // Close just the list (not a window it's in).
            e.preventDefault();
            setOpen(false);
        };
        // What's around it scrolling (not its own list) or the screen resizing: it'd be left behind, so it closes.
        const onScroll = (e: Event) => !list.current?.contains(e.target as Node) && setOpen(false);
        const onResize = () => setOpen(false);
        addEventListener("scroll", onScroll, true);
        addEventListener("resize", onResize);
        document.addEventListener("pointerdown", onDown, true);
        document.addEventListener("keydown", onKey, true);
        return () => {
            removeEventListener("scroll", onScroll, true);
            removeEventListener("resize", onResize);
            document.removeEventListener("pointerdown", onDown, true);
            document.removeEventListener("keydown", onKey, true);
        };
    }, [open]);

    const pick = (value: string) => {
        props.onChange(value);
        setOpen(false);
        setQuery("");
    };

    return (
        <div ref={box} className={clsx("relative", props.className)}>
            <button
                type="button"
                aria-label={props.label}
                aria-expanded={open}
                disabled={props.disabled}
                onClick={() => (open ? setOpen(false) : openList())}
                className="input flex cursor-pointer items-center gap-2 text-left disabled:cursor-default"
            >
                {current && !current.notRecord && current.synced !== undefined ? <SyncBadge synced={current.synced} /> : null}
                <span className={clsx("min-w-0 flex-1 truncate", !current && "text-muted")}>{current?.label ?? props.placeholder ?? "Choose…"}</span>
                <ChevronDown className="size-4 text-muted" />
            </button>
            {open ? (
                <div ref={list} style={place} className="card fixed z-50 overflow-y-auto p-1">
                    {props.options.length > 8 ? (
                        <label className="relative mb-1 block">
                            <span className="sr-only">Search</span>
                            <Search className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-muted" />
                            {/* Opened to pick one: the cursor's in its search. */}
                            <input className="input py-1.5 pl-8" placeholder="Search" autoFocus value={query} onChange={(e) => setQuery(e.target.value)} />
                        </label>
                    ) : null}
                    {shown.map((o) => (
                        <button
                            key={o.value}
                            type="button"
                            onClick={() => pick(o.value)}
                            className="flex w-full cursor-pointer items-center gap-2 rounded px-2.5 py-1.5 text-left text-(length:--field-text) hover:bg-surface-2"
                        >
                            <OptionMark option={o} />
                            <span className="min-w-0 flex-1 truncate">{o.label}</span>
                            {o.detail ? <span className="text-xs text-muted">{o.detail}</span> : null}
                            {o.value === props.value ? <Check className="size-4 text-brand-600" /> : null}
                        </button>
                    ))}
                    {shown.length === 0 ? <p className="px-2.5 py-2 text-(length:--field-text) text-muted">Nothing matches.</p> : null}
                </div>
            ) : null}
        </div>
    );
}

/** Before an option: its sync icon, a gap the same size (not a record, beside ones that are), or nothing. */
function OptionMark({ option }: Readonly<{ option: SyncOption }>) {
    if (option.notRecord) return <span className="inline-block w-3.5 shrink-0" />;
    return option.synced === undefined ? null : <SyncBadge synced={option.synced} />;
}
